import { useState } from "react";

export interface WriteOp {
  label: string;
  /**
   * Wo die Operation lebt. ``AGENT`` heisst: in einer Agent-Session, nicht am
   * Terminal — der Weg, den ein Realm-Owner nimmt, der die CLI beim Bootstrap
   * bekommen hat und sie von einem Agenten bedienen laesst (D-GOV-25). Er setzt
   * voraus, dass der Agent die noetige Anleitung auch ausgeliefert bekommt.
   */
  channel: "CLI" | "HTTP" | "MCP" | "AGENT";
  snippet: string;
}

interface Props {
  title?: string;
  ops: WriteOp[];
}

const CHANNEL_COLOR: Record<WriteOp["channel"], string> = {
  CLI: "text-kw-l1",
  HTTP: "text-kw-l2",
  MCP: "text-kw-l3",
  AGENT: "text-kw-l4",
};

/** Reihenfolge der Kopfzeilen-Nennung — stabil, damit die Zeile nicht mit der
 *  Sortierung der Ops wandert. */
const CHANNEL_ORDER: WriteOp["channel"][] = ["AGENT", "CLI", "HTTP", "MCP"];

const CHANNEL_NOUN: Record<WriteOp["channel"], string> = {
  CLI: "CLI",
  HTTP: "HTTP",
  MCP: "MCP",
  AGENT: "Agent-Session",
};

/**
 * Die Kanäle in der Kopfzeile werden aus ``ops`` **abgeleitet**, nie fest
 * verdrahtet: eine feste Aufzählung ist auf jedem Panel falsch, das einen der
 * genannten Kanäle nicht führt. Die Union oben zählt das Kanal-Vokabular auf,
 * nicht die belegte Teilmenge — ``MCP`` und ``AGENT`` führt heute kein Panel.
 */
export function channelSummary(ops: WriteOp[]): string {
  const present = CHANNEL_ORDER.filter((c) => ops.some((o) => o.channel === c));
  if (present.length === 0) return "";
  return ` — per ${present.map((c) => CHANNEL_NOUN[c]).join(" / ")}`;
}

export default function WriteOpsHint({
  title = "Write-Operationen",
  ops,
}: Props) {
  const [open, setOpen] = useState<boolean>(false);
  const [copied, setCopied] = useState<string>("");

  async function copy(snippet: string) {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(snippet);
      setTimeout(() => setCopied(""), 1500);
    } catch {
      // ignore — Clipboard-API kann in unsafe contexts blockiert sein
    }
  }

  return (
    <div className="mt-8 border border-kw-border-subtle rounded">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 bg-kw-bg-elevated text-left"
      >
        <span className="text-xs uppercase tracking-wide text-kw-text-muted">
          {title}{" "}
          <span className="text-kw-text-faint">
            (nicht in dieser UI{channelSummary(ops)})
          </span>
        </span>
        <span className="text-kw-text-muted text-xs">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="p-3 space-y-3 text-xs">
          {ops.length === 0 && (
            <div className="text-kw-text-faint">No out-of-UI operations for this view.</div>
          )}
          {ops.map((op, idx) => (
            <div key={idx} className="space-y-1">
              <div className="flex items-center gap-2">
                <span className={`font-mono ${CHANNEL_COLOR[op.channel]}`}>
                  {op.channel}
                </span>
                <span className="text-kw-text-primary">{op.label}</span>
                <button
                  onClick={() => copy(op.snippet)}
                  className="ml-auto text-kw-text-muted hover:text-kw-text-primary"
                >
                  {copied === op.snippet ? "✓ kopiert" : "copy"}
                </button>
              </div>
              <pre className="px-2 py-1 bg-kw-bg-overlay rounded text-kw-text-primary overflow-x-auto whitespace-pre-wrap break-all">
                {op.snippet}
              </pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
